import React from 'react';

// The signature element: where the platform runs, and what crosses the line.
//
// Ported verbatim from the mockup with var(--…) resolved to the site palette.
// Amber appears here and nowhere else on the route — it is what separates
// "inside your perimeter" from "outside it".
const ACCENT = '#22d3ee'; // cyan-400
const TEXT = '#f3f4f6'; // gray-100
const MUTED = '#9ca3af'; // gray-400
const SURFACE = '#1f2937'; // gray-800
const LINE = '#4b5563'; // gray-600
const WARM = '#fbbf24'; // amber-400 — external systems only

const MONO = 'Ubuntu Mono, ui-monospace, monospace';
const BODY = 'Gabarito, -apple-system, BlinkMacSystemFont, sans-serif';

const INSIDE = [
  { x: 70, y: 126, title: 'Web app · React', sub: 'your domain, your brand' },
  { x: 310, y: 126, title: 'Admin console', sub: 'users, plans, limits, keys' },
  { x: 70, y: 212, title: 'API · FastAPI', sub: 'REST · MCP · webhooks' },
  { x: 310, y: 212, title: 'Agent orchestrator', sub: 'research → setup → execute' },
  { x: 70, y: 298, title: 'Risk & policy engine', sub: 'sizing · SL/TP · approvals' },
  { x: 310, y: 298, title: 'Research workers', sub: 'news · on-chain · sentiment' },
  { x: 70, y: 384, title: 'Postgres', sub: 'recs · positions · audit log' },
  { x: 310, y: 384, title: 'Queue & object store', sub: 'jobs · artifacts · backups' },
];

const OUTSIDE = [
  { y: 139, title: 'Hyperliquid', sub: 'your builder code, your fees' },
  { y: 249, title: 'Model providers', sub: 'your keys — or your own GPUs' },
  { y: 359, title: 'Market & news data', sub: 'your subscriptions' },
];

export const DeploymentDiagram = () => (
  <figure className="m-0 rounded-xl border border-gray-700/60 bg-gray-800/40 p-5">
    {/* Wide content scrolls in its own container so the diagram stays legible
        on a phone instead of shrinking its labels to nothing. */}
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 920 560"
        role="img"
        aria-label="Diagram: the Katbot stack runs entirely inside your own cloud account. It reaches out only to Hyperliquid using your builder code, to model providers using your keys, and to market and news data feeds."
        className="block h-auto w-full min-w-[680px]"
      >
        <defs>
          <marker
            id="licensing-arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto"
          >
            <path d="M0 0 L10 5 L0 10 z" fill={LINE} />
          </marker>
        </defs>

        {/* your cloud boundary */}
        <text x="40" y="62" fontFamily={MONO} fontSize="12" letterSpacing="1.6" fill={ACCENT}>
          YOUR CLOUD ACCOUNT
        </text>
        <text x="40" y="80" fontFamily={BODY} fontSize="12" fill={MUTED}>
          AWS · GCP · Azure · bare metal — your VPC, your database, your secrets
        </text>
        <rect
          x="40"
          y="96"
          width="520"
          height="416"
          rx="16"
          fill="none"
          stroke={ACCENT}
          strokeWidth="1.4"
          strokeDasharray="7 6"
          opacity=".55"
        />

        {/* inner components */}
        <g fontFamily={BODY}>
          {INSIDE.map((box) => (
            <React.Fragment key={box.title}>
              <rect
                x={box.x}
                y={box.y}
                width="210"
                height="66"
                rx="10"
                fill={SURFACE}
                stroke={LINE}
              />
              <text x={box.x + 16} y={box.y + 26} fontSize="13.5" fontWeight="600" fill={TEXT}>
                {box.title}
              </text>
              <text x={box.x + 16} y={box.y + 46} fontSize="11.5" fill={MUTED}>
                {box.sub}
              </text>
            </React.Fragment>
          ))}
          <text x="70" y="484" fontSize="12" fill={MUTED}>
            Nothing crosses this line except the calls you authorize.
          </text>
        </g>

        {/* crossings — outbound only */}
        <g stroke={LINE} strokeWidth="1.3" markerEnd="url(#licensing-arrow)" fill="none">
          <path d="M562 172 H 636" />
          <path d="M562 282 H 636" />
          <path d="M562 392 H 636" />
        </g>

        {/* external systems */}
        <g fontFamily={BODY}>
          <text x="644" y="62" fontFamily={MONO} fontSize="12" letterSpacing="1.6" fill={WARM}>
            OUTBOUND ONLY
          </text>
          <text x="644" y="80" fontSize="12" fill={MUTED}>
            accounts you own
          </text>
          {OUTSIDE.map((box) => (
            <React.Fragment key={box.title}>
              <rect
                x="644"
                y={box.y}
                width="236"
                height="66"
                rx="10"
                fill="none"
                stroke={WARM}
                strokeOpacity=".45"
              />
              <text x="662" y={box.y + 26} fontSize="13.5" fontWeight="600" fill={TEXT}>
                {box.title}
              </text>
              <text x="662" y={box.y + 46} fontSize="11.5" fill={MUTED}>
                {box.sub}
              </text>
            </React.Fragment>
          ))}
        </g>
      </svg>
    </div>
    <figcaption className="mt-3.5 font-mono text-[0.74rem] tracking-[0.02em] text-gray-400">
      Single-tenant by design. No shared control plane, no phone-home, no customer data on our side.
    </figcaption>
  </figure>
);

export default DeploymentDiagram;
